import React, { useState, useEffect } from 'react';
import { Button, Modal, Typography, Space, Alert } from 'antd';
import { ReloadOutlined, WarningOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { resetAppCache } from '../utils/resetAppCache';
import { useServiceWorkerHealth } from '../hooks/useServiceWorkerHealth';

const { Title, Text } = Typography;

const RESET_COOLDOWN_KEY = 'lastResetTimestamp';
const RESET_COOLDOWN_MS = 5 * 60 * 1000;

const SNOOZE_KEY = 'swFixPromptSnoozeUntil';
const SNOOZE_MS = 60 * 60 * 1000;

const readSnoozeUntil = () => {
  try {
    const value = localStorage.getItem(SNOOZE_KEY);
    return value ? parseInt(value, 10) : 0;
  } catch {
    return 0;
  }
};

const readPreviousResetFailed = () => {
  try {
    return Boolean(localStorage.getItem(RESET_COOLDOWN_KEY));
  } catch {
    return false;
  }
};

const ServiceWorkerFixPrompt = () => {
  const broken = useServiceWorkerHealth();
  const [showModal, setShowModal] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [snoozedUntil, setSnoozedUntil] = useState(() => readSnoozeUntil());
  const [previousResetFailed] = useState(() => readPreviousResetFailed());
  const cooldownMinutes = Math.ceil(cooldownRemaining / 60000);
  const cooldownLabel = `${cooldownMinutes} minut${cooldownMinutes === 1 ? 'u' : 'e'}`;
  const isSnoozed = snoozedUntil > Date.now();

  useEffect(() => {
    if (!showModal) return undefined;

    const checkCooldown = () => {
      try {
        const lastReset = localStorage.getItem(RESET_COOLDOWN_KEY);
        if (lastReset) {
          const remaining =
            RESET_COOLDOWN_MS - (Date.now() - parseInt(lastReset, 10));
          if (remaining > 0) {
            setCooldownRemaining(remaining);
            return;
          }
        }
      } catch {}
      setCooldownRemaining(0);
    };

    checkCooldown();
    const interval = setInterval(checkCooldown, 1000);
    return () => clearInterval(interval);
  }, [showModal]);

  useEffect(() => {
    if (snoozedUntil <= Date.now()) return undefined;

    const timeout = setTimeout(() => {
      try {
        localStorage.removeItem(SNOOZE_KEY);
      } catch {}
      setSnoozedUntil(0);
    }, snoozedUntil - Date.now());

    return () => clearTimeout(timeout);
  }, [snoozedUntil]);

  const handleSnooze = () => {
    const until = Date.now() + SNOOZE_MS;
    try {
      localStorage.setItem(SNOOZE_KEY, until.toString());
    } catch {}
    setSnoozedUntil(until);
    setShowModal(false);
  };

  const handleReset = async () => {
    if (!navigator.onLine) {
      Modal.warning({
        title: 'Nema interneta',
        content:
          'Osvježavanje zahtijeva internet konekciju kako bi aplikacija mogla preuzeti najnoviju verziju.',
      });
      return;
    }

    if (cooldownRemaining > 0) {
      Modal.warning({
        title: 'Sačekajte malo',
        content: `Osvježavanje je već pokrenuto nedavno. Molimo sačekajte još ${cooldownLabel} prije ponovnog pokušaja.`,
      });
      return;
    }

    setResetting(true);
    console.warn('[ServiceWorker] User triggered manual reset via fix banner');

    try {
      localStorage.setItem(RESET_COOLDOWN_KEY, Date.now().toString());
    } catch {}

    await resetAppCache();
  };

  if (!broken || isSnoozed) return null;

  return (
    <>
      <div
        onClick={() => setShowModal(true)}
        style={{
          position: 'fixed',
          top: '25px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1002,
          backgroundColor: '#d4380d',
          padding: '4px 12px',
          borderRadius: '18px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          cursor: 'pointer',
        }}
      >
        <WarningOutlined style={{ color: '#fff', fontSize: '14px' }} />
        <span
          style={{
            color: '#fff',
            fontSize: '12px',
            fontWeight: 500,
            textAlign: 'center',
          }}
        >
          Problem sa aplikacijom
        </span>
      </div>

      {showModal && (
        <Modal
          visible={showModal}
          onCancel={() => !resetting && setShowModal(false)}
          footer={null}
          closable={!resetting}
          maskClosable={!resetting}
          centered
          width={400}
          bodyStyle={{ padding: '24px' }}
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <WarningOutlined
                style={{
                  fontSize: '48px',
                  color: '#d4380d',
                  marginBottom: '16px',
                }}
              />
              <Title level={4} style={{ marginBottom: '8px' }}>
                Aplikacija ima problem
              </Title>
              <Text type="secondary">
                Aplikacija je sporija nego inače i ne radi bez interneta.
                Brzi popravak je da je osvježite na najnoviju verziju.
              </Text>
            </div>

            <Alert
              type="info"
              showIcon
              message="Šta će se desiti?"
              description={
                <ul
                  style={{
                    marginLeft: '16px',
                    marginBottom: 0,
                    fontSize: '12px',
                  }}
                >
                  <li>Aplikacija će biti ažurirana na najnoviju verziju</li>
                  <li>
                    <strong>Vaše narudžbe i podaci ostaju netaknuti</strong>
                  </li>
                  <li>Moguće je da ćete se trebati ponovo prijaviti</li>
                </ul>
              }
            />

            {cooldownRemaining > 0 && (
              <Alert
                type="warning"
                showIcon
                icon={<ClockCircleOutlined />}
                message="Sačekajte malo"
                description={`Već ste pokušali da osvježite aplikaciju. Molimo sačekajte još ${cooldownLabel} prije ponovnog pokušaja.`}
              />
            )}

            {previousResetFailed && (
              <Alert
                type="warning"
                showIcon
                message="Osvježavanje ranije nije pomoglo"
                description="Ako se problem ponavlja, uradite potpuni reset. On briše sve lokalne podatke aplikacije i tražit će ponovnu prijavu."
              />
            )}

            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Button
                type="primary"
                size="large"
                block
                icon={<ReloadOutlined />}
                onClick={handleReset}
                loading={resetting}
                disabled={cooldownRemaining > 0}
                danger
              >
                Osvježi aplikaciju
              </Button>
              {previousResetFailed && (
                <Button
                  size="large"
                  block
                  danger
                  disabled={resetting}
                  onClick={() => {
                    window.location.href = '/api/app-reset.html';
                  }}
                >
                  Potpuni reset
                </Button>
              )}
              <Button
                block
                icon={<ClockCircleOutlined />}
                disabled={resetting}
                onClick={handleSnooze}
              >
                Odgodi na 1 sat
              </Button>
              <Button
                block
                type="text"
                disabled={resetting}
                onClick={() => setShowModal(false)}
              >
                Zatvori
              </Button>
            </Space>
          </Space>
        </Modal>
      )}
    </>
  );
};

export default ServiceWorkerFixPrompt;
